import { useContext } from 'react';

import {
  DisabledReason,
  GENERAL_OPERATIONS,
  PERM_MOD_CRM,
} from 'src/constants';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { useCustomerSubscription } from 'src/hooks/useCustomerSubscription';
import { usePlanStatus } from 'src/hooks/usePlanStatusHook';
import { useAppSelector } from 'src/hooks/useStore';
import { useGetDisabledPermissionsQuery } from 'src/services/api/permissionsApi';
import * as PlansUtils from 'src/utils/PlansUtils';
import { hasOperation } from 'src/utils/UserUtils';

export const useCreateClientPermission = () => {
  const { data: disabledPermissions } = useGetDisabledPermissionsQuery();
  const permissions = useAppSelector((state) => state.user.data?.permissions);
  const { EnableStudioPricing } = useFeatureFlags();
  const { isStudioUser } = useContext(PortalConfigContext);
  const { customerSubscriptions } = useCustomerSubscription();
  const { studio } = usePlanStatus();
  const hasCreatePermission = hasOperation(
    PERM_MOD_CRM,
    GENERAL_OPERATIONS.CREATE,
    permissions,
  );

  const isStudioOriginWorkspace =
    Boolean(isStudioUser) ||
    customerSubscriptions.some(PlansUtils.isStudioPlanSubscription);

  const reportedClientLimitReached = Boolean(
    disabledPermissions?.some(
      ({ permissionName, operationName, disabledReason }) => {
        return (
          permissionName === PERM_MOD_CRM &&
          operationName === GENERAL_OPERATIONS.CREATE &&
          disabledReason === DisabledReason.LimitReached
        );
      },
    ),
  );

  // Classic limits always honor the API. Studio caps roll out with `studio-pricing`
  // — the backend may report them earlier, but the UI stays off until the flag
  // is on for Studio-origin workspaces.
  const isCreateDisabledByPlanLimit =
    reportedClientLimitReached &&
    (!isStudioOriginWorkspace || EnableStudioPricing);

  const disabledReason =
    permissions === undefined
      ? undefined
      : !hasCreatePermission
        ? DisabledReason.PermissionDenied
        : isCreateDisabledByPlanLimit
          ? DisabledReason.LimitReached
          : undefined;

  return {
    canCreateClient: !disabledReason,
    disabledReason,
    /**
     * Studio prices seats, so a Studio workspace at its limit gets the
     * in-context upsell — an open menu that names the limit and links to plans —
     * in place of the Classic dead-end of a disabled button behind a tooltip.
     * Gated on `studio-pricing` alongside the limit itself.
     */
    showStudioClientLimitUpsell:
      studio.isStudioPricingWorkspace &&
      disabledReason === DisabledReason.LimitReached,
  };
};
